import React, {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import { Grid } from '../UIKit/system';
import { getThemeColor } from '../../utils/styles';

interface CompareImageProps {
  image?: { filename: string };
  secondImage?: { filename: string };
}

export const CompareImage: FC<CompareImageProps> = (props) => {
  const { image, secondImage } = props;
  const [show, setShow] = useState(false);

  if (!image?.filename) return null;

  return (
    <div>
      <Wrapper
        button={
          !!secondImage?.filename && {
            onClick: () => setShow((value) => !value)
          }
        }
      >
        <Image image={image} />

        {secondImage?.filename && (
          <Image image={secondImage} show={show} overlay />
        )}
      </Wrapper>

      {secondImage?.filename && (
        <DotsContainer>
          <NavDot
            onClick={() => setShow(false)}
            title="Show main image"
            aria-label="Show main image"
            active={!show}
          />
          <NavDot
            onClick={() => setShow(true)}
            title="Show secondary image"
            aria-label="Show secondary image"
            active={show}
          />
        </DotsContainer>
      )}
    </div>
  );
};

const Wrapper: FC<
  PropsWithChildren<{
    button?:
      | {
          onClick: MouseEventHandler<HTMLButtonElement>;
        }
      | false;
  }>
> = ({ button, children }) => {
  const label = 'Click or tap to toggle between images';

  if (button) {
    return (
      <Button onClick={button.onClick} title={label} aria-label={label}>
        {children}
      </Button>
    );
  }

  return <>{children}</>;
};

const Button = styled.button`
  border: none;
  outline: none;
  cursor: pointer;
  position: relative;
  width: 100%;
`;

const Image: FC<{
  image: { filename: string };
  overlay?: boolean;
  show?: boolean;
}> = (props) => {
  const { image, overlay, show } = props;

  return (
    <ImageContainer overlay={overlay} show={show}>
      <LazyImage
        fluid={getFluidGatsbyImage(image)}
        height={[160, 276]}
        width="100%"
      />
    </ImageContainer>
  );
};

const ImageContainer = styled.div<{ overlay?: boolean; show?: boolean }>`
  width: 100%;

  ${(props) =>
    props.overlay &&
    css`
      position: absolute;
      top: 0;
      opacity: 0;
      transition: all 0.3s ease-in-out;
      visibility: hidden;
    `}

  ${(props) =>
    props.show &&
    css`
      opacity: 1;
      visibility: visible;
    `}
`;

const DotsContainer = styled(Grid).attrs(() => ({
  gridAutoFlow: 'column',
  gridColumnGap: 5,
  alignContent: 'center',
  justifyContent: 'center',
  paddingTop: 15
}))``;

const NavDot = styled.button<{ active: boolean }>`
  background-color: ${getThemeColor('grey4')};
  display: block;
  border: none;
  border-radius: 100%;
  cursor: pointer;
  outline: none;
  height: 8px;
  width: 8px;

  ${(props) =>
    props.active &&
    css`
      background-color: ${getThemeColor('grey7')};
    `}
`;
